import { useContext, useEffect } from "react";
import "./App.scss";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import Home from "./pages/home/Home";
import JoinClass from "./pages/joinClass/JoinClass";
import CreateClass from "./pages/createClass/CreateClass";
import Login from "./pages/login/Login";
import Materials from "./pages/materials/Materials";
import Register from "./pages/register/Register";
import Tasks from "./pages/tasks/Tasks";
import Onlinemeet from "./pages/onlinemeet/Onlinemeet";
import IndividualMeterial from "./pages/individualMaterial/IndividualMeterial";
import IndividualTask from "./pages/individualTask/IndividualTask";
import Doubts from "./pages/doubts/Doubts";
import IndividualDoubt from "./pages/individualDoubt/IndividualDoubt";
import { AuthContext } from "./context/authContext/AuthContext";
import { logoutUser } from "./context/authContext/apiCalls";
import UserUpdate from "./pages/userUpdate/userUpdate"
import RegisteredByTeacher from "./pages/registeredbyteacher/RegisteredByTeacher";
import { io } from "socket.io-client";
function App() {
  const { user, isFetching, dispatch } = useContext(AuthContext);

  // Periodic check for account deletion (backup for socket.io) - most reliable
  useEffect(() => {
    if (!user?._id && !user?.id) return;

    const checkAccountStatus = async () => {
      try {
        const userId = user._id || user.id;
        const token = user.accessToken;
        
        // Use same origin as current page (handles IP addresses correctly)
        const baseURL = window.location.origin;
        const apiURL = `${baseURL}/api/user/${userId}`;
        
        console.log("[AccountCheck] Checking account status at:", apiURL);
        
        const response = await fetch(apiURL, {
          method: 'GET',
          headers: {
            token: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        console.log("[AccountCheck] Response status:", response.status);

        // If account deleted, user endpoint will return 404 or 500
        if (response.status === 404 || response.status === 500) {
          console.log("[AccountCheck] ✅ Account deleted - logging out from all devices");
          alert("Your account has been deleted by an administrator. You will be logged out.");
          logoutUser(dispatch);
          setTimeout(() => {
            window.location.href = "/login";
          }, 100);
        }
      } catch (error) {
        console.error("[AccountCheck] Error:", error.message);
      }
    };

    // Check every 3 seconds - very responsive
    const intervalId = setInterval(checkAccountStatus, 3000);
    
    // Initial check after 1 second
    const initialCheck = setTimeout(checkAccountStatus, 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(initialCheck);
    };
  }, [user?._id, user?.id, user?.accessToken, dispatch]);

  // Socket.io approach (real-time, primary method)
  useEffect(() => {
    if (!user?._id && !user?.id) return;

    // Use window.location.origin - works for IP addresses too
    const URL = window.location.origin;
    
    console.log("[ForceLogout] Connecting socket to:", URL);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const transports = isMobile ? ["polling"] : ["websocket", "polling"];
    
    console.log("[ForceLogout] Device type:", isMobile ? "Mobile" : "Desktop", "Transports:", transports);
    
    const socket = io(URL, {
      transports,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      timeout: 20000,
    });

    socket.on("connect", () => {
      console.log("[ForceLogout] ✅ Socket connected successfully!");
    });
    
    socket.on("connect_error", (error) => {
      console.error("[ForceLogout] ❌ Connection error:", error.message);
    });

    // Listen for force logout event
    socket.on("user:force-logout", (data) => {
      console.log("[ForceLogout] 🔔 Received logout event!");
      const myId = String(user._id || user.id);
      const deletedId = String(data.userId);
      
      console.log("[ForceLogout] ID comparison: my=" + myId + ", deleted=" + deletedId + ", match=" + (myId === deletedId));
      
      if (myId === deletedId) {
        console.log("[ForceLogout] ✅ Account deleted - logging out from all devices");
        alert("Your account has been deleted by an administrator. You will be logged out.");
        logoutUser(dispatch);
        setTimeout(() => {
          window.location.href = "/login";
        }, 100);
      }
    });

    socket.on("disconnect", (reason) => {
      console.log("[ForceLogout] Socket disconnected:", reason);
    });

    return () => {
      socket.off("user:force-logout");
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.disconnect();
    };
  }, [user?._id, user?.id, dispatch]);

  return (
    <div className="app">
      <Router>
        <Switch>
          <Route exact path="/">
            {user ? <Home /> : <Redirect to="/register" />}
          </Route>
          <Route path="/login">
            {!user ? <Login /> : <Redirect to="/" />}
          </Route>
          <Route path="/register">
            {!user ? <Register /> : <Redirect to="/" />}
          </Route>

          {user || isFetching ? (
            <>
              <Route path="/materials/:subject">
                <Materials dept="BCA" sem="5th" />
              </Route>
              <Route path="/material/:id">
                <IndividualMeterial />
              </Route>
              <Route path="/tasks/:subject">
                <Tasks dept="BCA" sem="5th" />
              </Route>
              <Route path="/task/:id">
                <IndividualTask />
              </Route>
              <Route path="/doubts/:subject">
                <Doubts dept="BCA" sem="5th" />
              </Route>
              <Route path="/doubt/:id">
                <IndividualDoubt />
              </Route>
              <Route exact path="/class/join/:classId">
                <JoinClass />
              </Route>
              <Route exact path="/class/:classId">
                <Onlinemeet />
              </Route>
              <Route path="/createclass">
                <CreateClass />
              </Route>
              <Route path="/userupdate">
                <UserUpdate />
              </Route>
              {(user?.isTeacher || user?.isAdmin) && (
                <Route path="/registeredbyteacher">
                  <RegisteredByTeacher />
                </Route>
              )}
            </>
          ) : (
            <Redirect to="/register" />
          )}
        </Switch>
      </Router>
    </div>
  );
}

export default App;
